import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { languages } from "../Data/languages";
import { clientName } from "../NewComponents/ClientWithCase";
import { englishCode } from "./languageCodes";

// Client portal redesign (v3): what the Orders, Quotes and Invoices lists of
// a client filter by ("Filters" next to the search box) and how.
//   Orders:   Status · Placed by (Team tab) · Language · Date
//   Quotes:   Status
//   Invoices: Status · Month · Placed by (Team tab)
// The same filter is a search-index filter (Algolia, production) and a test
// on the rows read from Firestore (development and staging have no index).
// Groups of statuses are OR'ed inside parentheses, so they never leak out of
// the account's own scope (the old filters built "AND a OR b").
// The filters live in the address (?status=&placedBy=&lang=&date=&month=),
// and "Clear" takes them out of it, keeping ?list=my|team.

export const ORDER_STATUS_GROUPS = [
  { key: "in-progress", label: "In progress", statuses: ["new", "assigned", "Assigned", "in-progress", "support-review", "revision", "on-hold"] },
  { key: "delivered", label: "Delivered", statuses: ["user-review"] },
  { key: "completed", label: "Completed", statuses: ["completed"] },
  { key: "cancelled", label: "Cancelled", statuses: ["cancelled", "refunded"] },
];

// New quotes are "in-review"; very old ones "submitted" (the old "In progress"
// filter only looked for "submitted").
export const QUOTE_STATUS_GROUPS = [
  { key: "ready", label: "Ready", statuses: ["replied"] },
  { key: "in-review", label: "In review", statuses: ["in-review", "submitted"] },
  { key: "ordered", label: "Ordered", statuses: ["ordered"] },
  { key: "cancelled", label: "Cancelled", statuses: ["cancelled"] },
  // Started and not sent yet (Data/Functions/draftQuote.js): "Continue" in the list.
  { key: "draft", label: "Draft", statuses: ["draft"] },
];

// "Paid" includes the ones office marked paid by hand (paid-manually).
export const INVOICE_STATUS_GROUPS = [
  { key: "unpaid", label: "Unpaid", statuses: ["pay-later"] },
  { key: "overdue", label: "Overdue", statuses: ["overdue"] },
  { key: "paid", label: "Paid", statuses: ["paid", "paid-manually"] },
  { key: "other", label: "On hold / cancelled", statuses: ["on-hold", "cancelled", "refunded"] },
];

export const STATUS_GROUPS = { orders: ORDER_STATUS_GROUPS, quotes: QUOTE_STATUS_GROUPS, invoices: INVOICE_STATUS_GROUPS };

export const DATE_RANGES = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 3 months", days: 90 },
  { key: "12m", label: "Last 12 months", days: 365 },
];

// The last 12 months, newest first: { key: "2026-09", label: "September 2026", start, end }.
export const recentMonths = (count = 12) => {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
    return { key, label: start.toLocaleDateString("en-US", { month: "long", year: "numeric" }), start: start.getTime(), end: end.getTime() - 1 };
  });
};

const monthRange = (key) => {
  const m = /^(\d{4})-(\d{2})$/.exec(key || "");
  if (!m) return null;
  const start = new Date(Number(m[1]), Number(m[2]) - 1, 1).getTime();
  const end = new Date(Number(m[1]), Number(m[2]), 1).getTime() - 1;
  return { start, end };
};

// From the start of that day (a fixed value, so the filter doesn't change
// on every render).
const dateRange = (key) => {
  const range = DATE_RANGES.find((r) => r.key === key);
  if (!range) return null;
  const today = new Date();
  return { start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - range.days).getTime(), end: null };
};

export const EMPTY_FILTERS = { status: "", placedBy: "", lang: "", date: "", month: "" };
const KEYS = Object.keys(EMPTY_FILTERS);

export const countFilters = (filters) => KEYS.filter((k) => filters[k]).length;

const q = (value) => `"${String(value).replace(/"/g, '\\"')}"`;
const anyOf = (field, values) => (values.length === 1 ? `${field}:${q(values[0])}` : `(${values.map((v) => `${field}:${q(v)}`).join(" OR ")})`);

// The index filter the lists add to the account's own scope.
export const indexFilter = (kind, filters) => {
  const parts = [];
  const group = (STATUS_GROUPS[kind] || []).find((g) => g.key === filters.status);
  if (group) parts.push(anyOf("status", group.statuses));
  if (filters.placedBy) parts.push(`userID:${q(filters.placedBy)}`);
  if (filters.lang) parts.push(`languageFrom:${q(filters.lang)}`);
  const range = kind === "invoices" ? monthRange(filters.month) : dateRange(filters.date);
  if (range) {
    parts.push(`timestamp >= ${Math.floor(range.start)}`);
    if (range.end) parts.push(`timestamp <= ${Math.floor(range.end)}`);
  }
  return parts.map((p) => ` AND ${p}`).join("");
};

// The same test on a row read from Firestore.
export const rowMatches = (kind, filters) => (row) => {
  const group = (STATUS_GROUPS[kind] || []).find((g) => g.key === filters.status);
  if (group && !group.statuses.includes(row.status)) return false;
  if (filters.placedBy && row.userID !== filters.placedBy) return false;
  if (filters.lang && row.languageFrom !== filters.lang) return false;
  const range = kind === "invoices" ? monthRange(filters.month) : dateRange(filters.date);
  if (range) {
    const at = Number(row.timestamp) || 0;
    if (at < range.start || (range.end && at > range.end)) return false;
  }
  return true;
};

// The search box on rows read from Firestore: the number, the client, the
// case number, who placed it, the first file's name.
export const rowSearch = (query) => {
  const words = String(query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return () => true;
  return (row) => {
    const hay = [
      row.orderNumber, row.quoteNumber, row.invoiceNumber, clientName(row), row.caseNumber, row.name, row.email,
      ...(Array.isArray(row.orders) ? row.orders.map((o) => o && o.number) : []),
      ...(Array.isArray(row.files) ? row.files.slice(0, 3).map((f) => f && f.name) : []),
    ].filter((v) => v !== undefined && v !== null).join(" ").toLowerCase();
    return words.every((w) => hay.includes(w.replace(/^#/, "")));
  };
};

const readFilters = (search) => {
  const params = new URLSearchParams(search);
  const out = { ...EMPTY_FILTERS };
  KEYS.forEach((k) => {
    const v = params.get(k);
    if (v && v.length < 120) out[k] = v;
  });
  return out;
};

// The list's filters, kept in the address. set(next) replaces them all;
// clear() takes them out of the address too (it kept them before).
export const useUrlFilters = () => {
  const location = useLocation();
  const history = useHistory();
  const filters = useMemo(() => readFilters(location.search), [location.search]);
  const set = useCallback((next) => {
    const params = new URLSearchParams(window.location.search);
    KEYS.forEach((k) => params.delete(k));
    KEYS.forEach((k) => {
      if (next[k]) params.set(k, next[k]);
    });
    const qs = params.toString();
    history.replace(`${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, [history]);
  const clear = useCallback(() => set(EMPTY_FILTERS), [set]);
  return { filters, set, clear, count: countFilters(filters) };
};

// The search box: what is typed now, and what is searched (a moment later).
export const useDebounced = (value, ms = 300) => {
  const [out, setOut] = useState(value);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(timer.current);
  }, [value, ms]);
  return out;
};

// ---------- Language codes ----------


export const languageName = (value) => {
  const found = languages.find((l) => l.value === value);
  if (found) return found.label;
  return value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : "";
};

const code = (value) => englishCode(value, languageName(value));

// "SP → EN" (title: "Spanish → English").
export const languagePair = (item) => ({
  short: item && item.languageFrom ? `${code(item.languageFrom)} → ${code(item.languageTo)}` : "—",
  long: item && item.languageFrom ? `${languageName(item.languageFrom)} → ${languageName(item.languageTo)}` : "",
});

export const languageOptions = () => [...languages].filter((l) => l.value !== "english").sort((a, b) => a.label.localeCompare(b.label));

// ---------- Small formats ----------

// "Oct 5" this year, "Nov 21, 2025" for an earlier (or later) year: the
// dates of the client's lists.
export const shortDate = (ms) => {
  if (!ms) return "";
  const at = new Date(Number(ms));
  if (Number.isNaN(at.getTime())) return "";
  const thisYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString("en-US", thisYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
};
export const longDate = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
export const dateTime = (ms) => (ms ? `${new Date(Number(ms)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}, ${new Date(Number(ms)).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "");

export const money = (dollars) => {
  const cents = Math.round((Number(dollars) || 0) * 100);
  return `${cents < 0 ? "−" : ""}$${(Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const moneyCents = (cents) => money((Number(cents) || 0) / 100);

// "acta-nacimiento.pdf +1": the first source file of an order or quote.
export const firstFile = (item) => {
  const files = Array.isArray(item && item.files) ? item.files.filter((f) => f && ["source", "client", "reference"].includes(f.type || "source")) : [];
  if (!files.length) return "";
  return files[0].name + (files.length > 1 ? ` +${files.length - 1}` : "");
};

// "View invoice" of an order, in a new tab: the invoice page when the order
// has its own invoice (case by case); otherwise (drafts aside) the order's
// printable invoice (orders on a team's monthly invoice, personal orders).
export const orderInvoiceHref = (order) => (order && order.orderInvoiceId && order.orderInvoiceType !== "multipleInvoice"
  ? `/account/invoice/${encodeURIComponent(order.orderInvoiceId)}`
  : `/account/print/order/${encodeURIComponent(order.id || order.objectID)}`);

export const newTab = (href) => ({ href, target: "_blank", rel: "noopener noreferrer", onMouseDown: (e) => e.preventDefault() });
